// src/pages/drone/buah-alami/DroneBuahAlami.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { useTheme } from "@mui/material/styles";
import "../../../components/css/ded-style.css";
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

import ReportBuahAlami from "./ReportBuahAlami";
import MapActionTooltip from "./components/MapActionTooltip";

// Import formatters dari file utilitas
import { formatNumber, formatDecimal, formatDateDisplay, formatText } from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import fungsi import Excel
import { importExcelToFirebase, refreshDroneData } from "./utils/importDataDroneBuahAlami";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Import fungsi calculation formula
import {
    calculateWeek,
    calculateTotalBuahAlami,
    calculatePopulasiTotal,
    calculatePercentageBuahAlami,
    calculateAllFormulas,
    calculateUmurHSFSaatMapping,
    calculateUmurHSFSaatIni,
    calculateUmurTerbang
} from "./formula/calculationFormula";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find((lokasi) => lokasi.id_lokasi === id_lokasi);
};

// Helper: Ambil nama kepala kebun plant care berdasarkan lokasi
const getKepalaKebunPlantCare = async (lokasiName, database, onValuePromise) => {
    try {
        // Cari data pj_wilayah berdasarkan lokasi
        const pjWilayahRef = ref(database, "db_config/db_helper/tb_pj_wilayah");
        const pjWilayahSnapshot = await onValuePromise(pjWilayahRef);
        const pjWilayahData = pjWilayahSnapshot.val();

        if (!pjWilayahData) return null;

        // Cari key yang sesuai dengan lokasi
        let foundIdKepalaKebunPlantCare = null;

        Object.keys(pjWilayahData).forEach((key) => {
            const wilayah = pjWilayahData[key];
            if (wilayah.lokasi === lokasiName) {
                foundIdKepalaKebunPlantCare = wilayah.id_kepala_kebun_plant_care;
            }
        });

        if (!foundIdKepalaKebunPlantCare) return null;

        // Cari nama kepala kebun plant care berdasarkan ID
        const kepalaKebunRef = ref(
            database,
            `db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care/${foundIdKepalaKebunPlantCare}`
        );
        const kepalaKebunSnapshot = await onValuePromise(kepalaKebunRef);
        const kepalaKebunData = kepalaKebunSnapshot.val();

        return kepalaKebunData ? kepalaKebunData.nama_kepala_kebun_plant_care : null;
    } catch (error) {
        console.error("Error fetching kepala kebun plant care:", error);
        return null;
    }
};

// Helper: Ambil nama kepala wilayah berdasarkan lokasi
const getKepalaWilayah = async (lokasiName, database, onValuePromise) => {
    try {
        // Cari data pj_wilayah berdasarkan lokasi
        const pjWilayahRef = ref(database, "db_config/db_helper/tb_pj_wilayah");
        const pjWilayahSnapshot = await onValuePromise(pjWilayahRef);
        const pjWilayahData = pjWilayahSnapshot.val();

        if (!pjWilayahData) return null;

        // Cari key yang sesuai dengan lokasi
        let foundIdKepalaWilayah = null;

        Object.keys(pjWilayahData).forEach((key) => {
            const wilayah = pjWilayahData[key];
            if (wilayah.lokasi === lokasiName) {
                foundIdKepalaWilayah = wilayah.id_kepala_wilayah;
            }
        });

        if (!foundIdKepalaWilayah) return null;

        // Cari nama kepala wilayah berdasarkan ID
        const kepalaWilayahRef = ref(
            database,
            `db_config/db_fresh_pineapple/tb_kepala_wilayah/${foundIdKepalaWilayah}`
        );
        const kepalaWilayahSnapshot = await onValuePromise(kepalaWilayahRef);
        const kepalaWilayahData = kepalaWilayahSnapshot.val();

        return kepalaWilayahData ? kepalaWilayahData.nama_kepala_wilayah : null;
    } catch (error) {
        console.error("Error fetching kepala wilayah:", error);
        return null;
    }
};

// Fungsi untuk mengambil data Real Forcing dari Setara Urea Per Umur
const getRealForcingFromSetaraUrea = async (id_lokasi, database, onValuePromise) => {
    try {
        // Fetch data from db_plantation/tb_setara_urea_per_umur
        const setaraUreaRef = ref(database, "db_plantation/tb_setara_urea_per_umur");
        const setaraUreaSnapshot = await onValuePromise(setaraUreaRef);
        const setaraUreaData = setaraUreaSnapshot.val();

        if (!setaraUreaData) return null;

        // Cari data dengan id_lokasi yang sesuai
        let foundRealForcing = null;

        Object.keys(setaraUreaData).forEach((key) => {
            const record = setaraUreaData[key];
            if (record.id_lokasi && record.id_lokasi.toString() === id_lokasi.toString()) {
                foundRealForcing = record.real_forcing || null;
            }
        });

        return foundRealForcing;
    } catch (error) {
        console.error("Error fetching real forcing from setara urea:", error);
        return null;
    }
};

// FUNGSI BARU: Ambil data Tanggal Closing dari Data Monitor Hasil Panen
const getTanggalClosingFromMonitorHasilPanen = async (id_lokasi, database, onValuePromise) => {
    try {
        // Fetch data from db_harvesting/tb_monitor_hasil_panen
        const monitorRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
        const monitorSnapshot = await onValuePromise(monitorRef);
        const monitorData = monitorSnapshot.val();

        if (!monitorData) return null;

        // Cari data dengan id_lokasi yang sesuai
        let foundTanggalClosing = null;

        Object.keys(monitorData).forEach((key) => {
            const record = monitorData[key];
            if (record.id_lokasi && record.id_lokasi.toString() === id_lokasi.toString()) {
                foundTanggalClosing = record.tanggal_closing || null;
            }
        });

        return foundTanggalClosing;
    } catch (error) {
        console.error("Error fetching tanggal closing from monitor hasil panen:", error);
        return null;
    }
};

// Fungsi untuk menghitung persentase buah alami (versi lama, akan diganti)
const calculateBuahAlamiPercentage = (earlyCones, dryPetals, buahAlami) => {
    try {
        const total = parseFloat(earlyCones || 0) + parseFloat(dryPetals || 0) + parseFloat(buahAlami || 0);
        if (total === 0) return "0%";

        const percentage = (parseFloat(buahAlami || 0) / total) * 100;
        return `${percentage.toFixed(2)}%`;
    } catch (error) {
        return "-";
    }
};

// Fungsi untuk menghitung week dari tanggal (untuk rencana saat panen)
const calculateWeekFromDate = (date) => {
    try {
        if (!date || date === "-") return "-";

        // Parse tanggal dari string
        let parsedDate;

        if (typeof date === "string" && date.includes("-")) {
            const parts = date.split("-");
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);

                // Validasi
                if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1900 || year > 2100) {
                    return "-";
                }

                parsedDate = new Date(year, month, day);

                // Validasi lagi untuk kasus seperti 31 Februari
                if (
                    parsedDate.getDate() !== day ||
                    parsedDate.getMonth() !== month ||
                    parsedDate.getFullYear() !== year
                ) {
                    return "-";
                }
            } else {
                parsedDate = new Date(date);
            }
        } else {
            parsedDate = new Date(date);
        }

        if (isNaN(parsedDate.getTime())) {
            return "-";
        }

        // IMPLEMENTASI PERSIS WEEKNUM Excel dengan return_type = 1
        // Week dimulai hari Minggu, week 1 adalah week yang mengandung 1 Januari

        const year = parsedDate.getFullYear();

        // Dapatkan 1 Januari tahun ini
        const jan1 = new Date(year, 0, 1);

        // Hari dalam minggu untuk 1 Januari (0=Minggu, 1=Senin, ..., 6=Sabtu)
        const jan1DayOfWeek = jan1.getDay();

        // Hitung offset untuk week 1
        // Jika 1 Januari bukan Minggu, maka hari-hari sebelum Minggu pertama masih week 1
        let week1Start;
        if (jan1DayOfWeek === 0) {
            // 1 Januari adalah Minggu
            week1Start = jan1;
        } else {
            // Week 1 dimulai dari Minggu sebelum atau pada 1 Januari
            const daysToPrevSunday = jan1DayOfWeek; // Karena 0=Minggu
            week1Start = new Date(year, 0, 1 - daysToPrevSunday);
        }

        // Hitung selisih hari antara tanggal dan awal week 1
        const diffMs = parsedDate.getTime() - week1Start.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        // Hitung week number
        const weekNum = Math.floor(diffDays / 7) + 1;

        // Pastikan week number valid
        if (weekNum < 1 || weekNum > 53) {
            return "-";
        }

        return `Week ${weekNum}`;
    } catch (error) {
        console.error("Error in calculateWeekFromDate:", error, "for date:", date);
        return "-";
    }
};

// Fungsi untuk menghitung tanggal baru dengan menambahkan hari
const calculateNewDate = (baseDate, daysToAdd) => {
    try {
        if (!baseDate || baseDate === "-") return "-";

        // Parse tanggal dari string
        let parsedDate;

        if (typeof baseDate === "string" && baseDate.includes("-")) {
            const parts = baseDate.split("-");
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);

                // Validasi
                if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1900 || year > 2100) {
                    return "-";
                }

                parsedDate = new Date(year, month, day);

                // Validasi lagi untuk kasus seperti 31 Februari
                if (
                    parsedDate.getDate() !== day ||
                    parsedDate.getMonth() !== month ||
                    parsedDate.getFullYear() !== year
                ) {
                    return "-";
                }
            } else {
                parsedDate = new Date(baseDate);
            }
        } else {
            parsedDate = new Date(baseDate);
        }

        if (isNaN(parsedDate.getTime())) {
            return "-";
        }

        // Tambahkan hari
        const newDate = new Date(parsedDate);
        newDate.setDate(newDate.getDate() + daysToAdd);

        // Hitung week dari tanggal baru
        return calculateWeekFromDate(newDate);
    } catch (error) {
        console.error("Error in calculateNewDate:", error);
        return "-";
    }
};

// Helper: format tanggal Real Forcing untuk ditampilkan
const formatRealForcingForDisplay = (realForcing) => {
    if (!realForcing || realForcing === "" || realForcing === "-") return "-";

    // Normalisasi string
    const normalized = realForcing.toString().trim();

    // Jika sudah format DD-MM-YYYY, biarkan as is
    if (normalized.includes("-") && normalized.split("-").length === 3) {
        const parts = normalized.split("-");
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return normalized;
        }
    }

    // Jika format DD/MM/YYYY, ubah ke DD-MM-YYYY
    if (normalized.includes("/") && normalized.split("/").length === 3) {
        const parts = normalized.split("/");
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
    }

    // Jika format YYYY-MM-DD, ubah ke DD-MM-YYYY
    if (normalized.includes("-") && normalized.split("-").length === 3) {
        const parts = normalized.split("-");
        if (parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
    }

    // Coba parse sebagai Date object
    try {
        const date = new Date(normalized);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        }
    } catch (e) {
        // ignore
    }

    return normalized; // Return as is jika format tidak dikenali
};

// Fungsi untuk mengurutkan data berdasarkan tanggal terbang terbaru
const sortDataByTanggalTerbang = (dataList) => {
    return [...dataList].sort((a, b) => {
        // Parse tanggal untuk perbandingan
        const parseDate = (dateStr) => {
            if (!dateStr || dateStr === "-") return new Date(0); // Tanggal paling lama

            try {
                // Coba parse format DD-MM-YYYY
                if (dateStr.includes("-")) {
                    const parts = dateStr.split("-");
                    if (parts.length === 3) {
                        const day = parseInt(parts[0], 10);
                        const month = parseInt(parts[1], 10) - 1;
                        const year = parseInt(parts[2], 10);

                        // Validasi
                        if (day < 1 || day > 31 || month < 0 || month > 11 || year < 1900 || year > 2100) {
                            return new Date(0);
                        }

                        const date = new Date(year, month, day);
                        if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
                            return date;
                        }
                    }
                }

                // Coba parse sebagai Date object
                const date = new Date(dateStr);
                if (!isNaN(date.getTime())) {
                    return date;
                }

                return new Date(0);
            } catch (error) {
                return new Date(0);
            }
        };

        const dateA = parseDate(a.tanggal_terbang);
        const dateB = parseDate(b.tanggal_terbang);

        // Urutkan dari yang terbaru ke terlama (descending)
        return dateB - dateA;
    });
};

function DroneBuahAlami() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // State untuk data konfigurasi dropdown
    const [lokasiData, setLokasiData] = useState([]);
    const [kepalaKebunPlantCareCache, setKepalaKebunPlantCareCache] = useState({});
    const [kepalaWilayahCache, setKepalaWilayahCache] = useState({});

    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filter states
    const [filterTahun, setFilterTahun] = useState("");

    // State untuk cache Real Forcing
    const [realForcingCache, setRealForcingCache] = useState({});

    // State untuk cache Tanggal Closing
    const [tanggalClosingCache, setTanggalClosingCache] = useState({});

    const handleDataUpdated = async () => {
        try {
            // Refresh data setelah update peta
            const finalData = await refreshDataAfterImport();
            setDataList(finalData);
            setOriginalDataList(finalData);
        } catch (error) {
            console.error("Error refreshing data after map update:", error);
            setSnackbar({
                open: true,
                message: "Gagal merefresh data setelah update peta",
                severity: "error",
            });
        }
    };

    // Fungsi untuk mengambil nama kepala kebun plant care
    const fetchKepalaKebunPlantCare = async (lokasiName) => {
        // Cek cache dulu
        if (kepalaKebunPlantCareCache[lokasiName]) {
            return kepalaKebunPlantCareCache[lokasiName];
        }

        // Jika tidak ada di cache, fetch dari Firebase
        const namaKepalaKebun = await getKepalaKebunPlantCare(lokasiName, database, onValuePromise);

        // Update cache
        if (namaKepalaKebun) {
            setKepalaKebunPlantCareCache((prev) => ({
                ...prev,
                [lokasiName]: namaKepalaKebun,
            }));
        }

        return namaKepalaKebun;
    };

    // Fungsi untuk mengambil nama kepala wilayah
    const fetchKepalaWilayah = async (lokasiName) => {
        // Cek cache dulu
        if (kepalaWilayahCache[lokasiName]) {
            return kepalaWilayahCache[lokasiName];
        }

        // Jika tidak ada di cache, fetch dari Firebase
        const namaKepalaWilayah = await getKepalaWilayah(lokasiName, database, onValuePromise);

        // Update cache
        if (namaKepalaWilayah) {
            setKepalaWilayahCache((prev) => ({
                ...prev,
                [lokasiName]: namaKepalaWilayah,
            }));
        }

        return namaKepalaWilayah;
    };

    // Fungsi untuk mengambil Real Forcing dari Setara Urea Per Umur
    const fetchRealForcingFromSetaraUrea = async (id_lokasi) => {
        // Cek cache dulu
        if (realForcingCache[id_lokasi] !== undefined) {
            return realForcingCache[id_lokasi];
        }

        // Jika tidak ada di cache, fetch dari Firebase
        const realForcing = await getRealForcingFromSetaraUrea(id_lokasi, database, onValuePromise);

        // Update cache (simpan null jika tidak ditemukan)
        setRealForcingCache((prev) => ({
            ...prev,
            [id_lokasi]: realForcing,
        }));

        return realForcing;
    };

    // FUNGSI BARU: Ambil Tanggal Closing dari Monitor Hasil Panen
    const fetchTanggalClosingFromMonitorHasilPanen = async (id_lokasi) => {
        // Cek cache dulu
        if (tanggalClosingCache[id_lokasi] !== undefined) {
            return tanggalClosingCache[id_lokasi];
        }

        // Jika tidak ada di cache, fetch dari Firebase
        const tanggalClosing = await getTanggalClosingFromMonitorHasilPanen(id_lokasi, database, onValuePromise);

        // Update cache (simpan null jika tidak ditemukan)
        setTanggalClosingCache((prev) => ({
            ...prev,
            [id_lokasi]: tanggalClosing,
        }));

        return tanggalClosing;
    };

    // Fungsi untuk mengambil semua data terkait lokasi
    const fetchLokasiData = async (lokasiName) => {
        const [kepalaKebunPlantCare, kepalaWilayah] = await Promise.all([
            fetchKepalaKebunPlantCare(lokasiName),
            fetchKepalaWilayah(lokasiName),
        ]);

        return {
            kepalaKebunPlantCare,
            kepalaWilayah,
        };
    };

    // Fungsi untuk menghitung rencana saat panen
    const calculateRencanaSaatPanen = (tanggalTerbang) => {
        if (!tanggalTerbang || tanggalTerbang === "-") {
            return {
                earlyCones: "-",
                dryPetals: "-",
                buah: "-",
            };
        }

        // Tanggal Terbang + 85 hari untuk Early Cones
        const earlyConesWeek = calculateNewDate(tanggalTerbang, 85);

        // Tanggal Terbang + 55 hari untuk Dry Petals
        const dryPetalsWeek = calculateNewDate(tanggalTerbang, 55);

        // Tanggal Terbang + 30 hari untuk Buah
        const buahWeek = calculateNewDate(tanggalTerbang, 30);

        return {
            earlyCones: earlyConesWeek,
            dryPetals: dryPetalsWeek,
            buah: buahWeek,
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_drone/tb_buah_alami
                const droneRef = ref(database, "db_drone/tb_buah_alami");
                const droneSnapshot = await onValuePromise(droneRef);
                const droneData = droneSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;

                if (droneData) {
                    Object.keys(droneData).forEach((key) => {
                        const record = droneData[key];
                        loadedData.push({
                            firebaseKey: key,
                            // Data dari Excel yang disimpan di Firebase
                            id_lokasi: record.id_lokasi || "",
                            tanggal_terbang: record.tanggal_terbang || "-",
                            tanggal_selesai_identifikasi: record.tanggal_selesai_identifikasi || "-",
                            early_cones: record.early_cones || "-",
                            dry_petals: record.dry_petals || "-",
                            buah_alami: record.buah_alami || "-",
                            populasi_per_ha: record.populasi_per_ha || "-",
                        });

                        // Cari waktu update terakhir
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });

                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Fetch data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();

                let lokasiArray = [];
                if (lokasiRawData) {
                    // Konversi dari object Firebase ke array
                    lokasiArray = Object.keys(lokasiRawData).map((key) => ({
                        firebaseKey: key,
                        ...lokasiRawData[key],
                    }));
                    setLokasiData(lokasiArray);

                    // Update dataList dengan informasi dari tb_lokasi
                    const updatedDataList = await Promise.all(
                        loadedData.map(async (item) => {
                            const lokasiInfo = findLokasiById(lokasiArray, item.id_lokasi);

                            // Ambil Real Forcing dari Setara Urea Per Umur berdasarkan id_lokasi
                            const realForcingFromSetaraUrea = await fetchRealForcingFromSetaraUrea(item.id_lokasi);

                            // AMBIL TANGGAL CLOSING dari Monitor Hasil Panen berdasarkan id_lokasi
                            const tanggalClosingFromMonitor = await fetchTanggalClosingFromMonitorHasilPanen(
                                item.id_lokasi
                            );

                            // HITUNG UMUR - SAAT INI menggunakan fungsi dari calculationFormula.js
                            const umurHSFSaatIni = calculateUmurHSFSaatIni(
                                realForcingFromSetaraUrea,
                                tanggalClosingFromMonitor,
                                new Date() // tanggal saat ini
                            );

                            // HITUNG UMUR - SAAT MAPPING menggunakan fungsi dari calculationFormula.js
                            const umurHSFSaatMapping = calculateUmurHSFSaatMapping(
                                item.tanggal_selesai_identifikasi,
                                realForcingFromSetaraUrea
                            );

                            // AMBIL DATA MULAI_RAWAT dari tb_lokasi
                            const mulai_rawat = lokasiInfo ? lokasiInfo.mulai_rawat || null : null;

                            let baseData = {};

                            if (lokasiInfo) {
                                // Ambil data dari tb_lokasi
                                const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                                const displayLuas = lokasiInfo.netto || "-";
                                const displayStatus = lokasiInfo.status || "";
                                const displayTahun = lokasiInfo.tahun || "";
                                const displayMulaiRawat = lokasiInfo.mulai_rawat || "-";

                                // Default kepala kebun dan kepala wilayah dari tb_lokasi
                                const defaultKepalaKebun = lokasiInfo.kepala_kebun || "";
                                const defaultKepalaWilayah = lokasiInfo.kepala_wilayah || "";

                                // Coba ambil kepala kebun plant care dan kepala wilayah dari tb_pj_wilayah
                                const lokasiAdditionalData = displayLokasi
                                    ? await fetchLokasiData(displayLokasi)
                                    : {
                                          kepalaKebunPlantCare: null,
                                          kepalaWilayah: null,
                                      };

                                // Gunakan kepala kebun plant care jika ada, jika tidak gunakan default dari tb_lokasi
                                const finalKepalaKebun =
                                    lokasiAdditionalData.kepalaKebunPlantCare || defaultKepalaKebun;

                                // Gunakan kepala wilayah dari tb_pj_wilayah jika ada, jika tidak gunakan default dari tb_lokasi
                                const finalKepalaWilayah = lokasiAdditionalData.kepalaWilayah || defaultKepalaWilayah;

                                // Hitung semua formula menggunakan calculationFormula dengan parameter mulai_rawat
                                const calculations = calculateAllFormulas(
                                    {
                                        ...item,
                                        luas_netto: displayLuas,
                                    },
                                    mulai_rawat
                                );

                                // Hitung rencana saat panen
                                const rencanaSaatPanen = calculateRencanaSaatPanen(item.tanggal_terbang);

                                // Format Real Forcing untuk ditampilkan
                                const displayRealForcing = realForcingFromSetaraUrea
                                    ? formatRealForcingForDisplay(realForcingFromSetaraUrea)
                                    : "-";

                                baseData = {
                                    ...item,
                                    // Data dari tb_lokasi
                                    lokasi: displayLokasi,
                                    luas_netto: displayLuas,
                                    status: displayStatus,
                                    tahun: displayTahun,
                                    kepala_kebun: finalKepalaKebun,
                                    kepala_wilayah: finalKepalaWilayah,
                                    // Data mulai_rawat dari tb_lokasi
                                    mulai_rawat: displayMulaiRawat,
                                    // Data Real Forcing dari Setara Urea Per Umur
                                    real_forcing: displayRealForcing,
                                    // Data calculated dari formula
                                    week: calculations.week,
                                    total_buah_alami: calculations.totalBuahAlami,
                                    populasi_total: calculations.populasiTotal,
                                    percentage_early_cones: calculations.percentageBuahAlami.earlyConesPercentage,
                                    percentage_dry_petals: calculations.percentageBuahAlami.dryPetalsPercentage,
                                    percentage_buah_alami: calculations.percentageBuahAlami.buahAlamiPercentage,
                                    percentage_total_buah_alami: calculations.percentageBuahAlami.totalPercentage,
                                    // Persentase berdasarkan populasi total
                                    percentage_by_populasi_early_cones:
                                        calculations.percentagesByPopulasi.earlyConesPercentage,
                                    percentage_by_populasi_dry_petals:
                                        calculations.percentagesByPopulasi.dryPetalsPercentage,
                                    percentage_by_populasi_buah_alami:
                                        calculations.percentagesByPopulasi.buahAlamiPercentage,
                                    percentage_by_populasi_total: calculations.percentagesByPopulasi.totalPercentage,
                                    // Data rencana saat panen
                                    rencana_early_cones: rencanaSaatPanen.earlyCones,
                                    rencana_dry_petals: rencanaSaatPanen.dryPetals,
                                    rencana_buah: rencanaSaatPanen.buah,
                                    // UMUR - SAAT INI
                                    umur_hsf_saat_ini: umurHSFSaatIni,
                                    // UMUR - SAAT MAPPING
                                    umur_hsf_saat_mapping: umurHSFSaatMapping,
                                    // UMUR - TERBANG (ditambahkan) - sekarang dalam format object
                                    umur_terbang_hari: calculations.umurTerbang.hari,
                                    umur_terbang_bulan: calculations.umurTerbang.bulan,
                                    umur_terbang_display: calculations.umurTerbang.display,
                                };
                            } else {
                                // Jika tidak ditemukan di tb_lokasi
                                const displayLokasi = item.id_lokasi || "";
                                const lokasiAdditionalData = displayLokasi
                                    ? await fetchLokasiData(displayLokasi)
                                    : {
                                          kepalaKebunPlantCare: null,
                                          kepalaWilayah: null,
                                      };

                                // Hitung semua formula menggunakan calculationFormula (tanpa mulai_rawat)
                                const calculations = calculateAllFormulas(item);

                                // Hitung rencana saat panen
                                const rencanaSaatPanen = calculateRencanaSaatPanen(item.tanggal_terbang);

                                // Format Real Forcing untuk ditampilkan
                                const displayRealForcing = realForcingFromSetaraUrea
                                    ? formatRealForcingForDisplay(realForcingFromSetaraUrea)
                                    : "-";

                                baseData = {
                                    ...item,
                                    lokasi: displayLokasi,
                                    luas_netto: "-",
                                    status: "",
                                    tahun: "",
                                    mulai_rawat: "-",
                                    kepala_kebun: lokasiAdditionalData.kepalaKebunPlantCare || "",
                                    kepala_wilayah: lokasiAdditionalData.kepalaWilayah || "",
                                    // Data Real Forcing dari Setara Urea Per Umur
                                    real_forcing: displayRealForcing,
                                    // Data calculated dari formula
                                    week: calculations.week,
                                    total_buah_alami: calculations.totalBuahAlami,
                                    populasi_total: calculations.populasiTotal,
                                    percentage_early_cones: calculations.percentageBuahAlami.earlyConesPercentage,
                                    percentage_dry_petals: calculations.percentageBuahAlami.dryPetalsPercentage,
                                    percentage_buah_alami: calculations.percentageBuahAlami.buahAlamiPercentage,
                                    percentage_total_buah_alami: calculations.percentageBuahAlami.totalPercentage,
                                    // Persentase berdasarkan populasi total
                                    percentage_by_populasi_early_cones:
                                        calculations.percentagesByPopulasi.earlyConesPercentage,
                                    percentage_by_populasi_dry_petals:
                                        calculations.percentagesByPopulasi.dryPetalsPercentage,
                                    percentage_by_populasi_buah_alami:
                                        calculations.percentagesByPopulasi.buahAlamiPercentage,
                                    percentage_by_populasi_total: calculations.percentagesByPopulasi.totalPercentage,
                                    // Data rencana saat panen
                                    rencana_early_cones: rencanaSaatPanen.earlyCones,
                                    rencana_dry_petals: rencanaSaatPanen.dryPetals,
                                    rencana_buah: rencanaSaatPanen.buah,
                                    // UMUR - SAAT INI
                                    umur_hsf_saat_ini: umurHSFSaatIni,
                                    // UMUR - SAAT MAPPING
                                    umur_hsf_saat_mapping: umurHSFSaatMapping,
                                    // UMUR - TERBANG (ditambahkan) - sekarang dalam format object
                                    umur_terbang_hari: calculations.umurTerbang.hari,
                                    umur_terbang_bulan: calculations.umurTerbang.bulan,
                                    umur_terbang_display: calculations.umurTerbang.display,
                                };
                            }

                            return baseData;
                        })
                    );

                    // PERUBAHAN: Urutkan data berdasarkan tanggal terbang terbaru
                    const sortedData = sortDataByTanggalTerbang(updatedDataList);

                    setOriginalDataList(sortedData);
                    setDataList(sortedData);
                } else {
                    // Jika tb_lokasi kosong, gunakan data dasar
                    const basicDataList = await Promise.all(
                        loadedData.map(async (item) => {
                            const displayLokasi = item.id_lokasi || "";
                            const lokasiAdditionalData = displayLokasi
                                ? await fetchLokasiData(displayLokasi)
                                : {
                                      kepalaKebunPlantCare: null,
                                      kepalaWilayah: null,
                                  };

                            // Ambil Real Forcing dari Setara Urea Per Umur berdasarkan id_lokasi
                            const realForcingFromSetaraUrea = await fetchRealForcingFromSetaraUrea(item.id_lokasi);

                            // AMBIL TANGGAL CLOSING dari Monitor Hasil Panen berdasarkan id_lokasi
                            const tanggalClosingFromMonitor = await fetchTanggalClosingFromMonitorHasilPanen(
                                item.id_lokasi
                            );

                            // HITUNG UMUR - SAAT INI menggunakan fungsi dari calculationFormula.js
                            const umurHSFSaatIni = calculateUmurHSFSaatIni(
                                realForcingFromSetaraUrea,
                                tanggalClosingFromMonitor,
                                new Date() // tanggal saat ini
                            );

                            // HITUNG UMUR - SAAT MAPPING menggunakan fungsi dari calculationFormula.js
                            const umurHSFSaatMapping = calculateUmurHSFSaatMapping(
                                item.tanggal_selesai_identifikasi,
                                realForcingFromSetaraUrea
                            );

                            // Hitung semua formula menggunakan calculationFormula (tanpa mulai_rawat)
                            const calculations = calculateAllFormulas(item);

                            // Hitung rencana saat panen
                            const rencanaSaatPanen = calculateRencanaSaatPanen(item.tanggal_terbang);

                            // Format Real Forcing untuk ditampilkan
                            const displayRealForcing = realForcingFromSetaraUrea
                                ? formatRealForcingForDisplay(realForcingFromSetaraUrea)
                                : "-";

                            const baseData = {
                                ...item,
                                lokasi: displayLokasi,
                                luas_netto: "-",
                                status: "",
                                tahun: "",
                                mulai_rawat: "-",
                                kepala_kebun: lokasiAdditionalData.kepalaKebunPlantCare || "",
                                kepala_wilayah: lokasiAdditionalData.kepalaWilayah || "",
                                // Data Real Forcing dari Setara Urea Per Umur
                                real_forcing: displayRealForcing,
                                // Data calculated dari formula
                                week: calculations.week,
                                total_buah_alami: calculations.totalBuahAlami,
                                populasi_total: calculations.populasiTotal,
                                percentage_early_cones: calculations.percentageBuahAlami.earlyConesPercentage,
                                percentage_dry_petals: calculations.percentageBuahAlami.dryPetalsPercentage,
                                percentage_buah_alami: calculations.percentageBuahAlami.buahAlamiPercentage,
                                percentage_total_buah_alami: calculations.percentageBuahAlami.totalPercentage,
                                // Persentase berdasarkan populasi total
                                percentage_by_populasi_early_cones:
                                    calculations.percentagesByPopulasi.earlyConesPercentage,
                                percentage_by_populasi_dry_petals:
                                    calculations.percentagesByPopulasi.dryPetalsPercentage,
                                percentage_by_populasi_buah_alami:
                                    calculations.percentagesByPopulasi.buahAlamiPercentage,
                                percentage_by_populasi_total: calculations.percentagesByPopulasi.totalPercentage,
                                // Data rencana saat panen
                                rencana_early_cones: rencanaSaatPanen.earlyCones,
                                rencana_dry_petals: rencanaSaatPanen.dryPetals,
                                rencana_buah: rencanaSaatPanen.buah,
                                // UMUR - SAAT INI
                                umur_hsf_saat_ini: umurHSFSaatIni,
                                // UMUR - SAAT MAPPING
                                umur_hsf_saat_mapping: umurHSFSaatMapping,
                                // UMUR - TERBANG (ditambahkan) - sekarang dalam format object
                                umur_terbang_hari: calculations.umurTerbang.hari,
                                umur_terbang_bulan: calculations.umurTerbang.bulan,
                                umur_terbang_display: calculations.umurTerbang.display,
                            };

                            return baseData;
                        })
                    );

                    // PERUBAHAN: Urutkan data berdasarkan tanggal terbang terbaru
                    const sortedData = sortDataByTanggalTerbang(basicDataList);

                    setOriginalDataList(sortedData);
                    setDataList(sortedData);
                    setLokasiData([]);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(
                (item) => item.lokasi && item.lokasi.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handle refresh data setelah import
    const refreshDataAfterImport = async () => {
        try {
            const updatedData = await refreshDroneData(database, onValuePromise);

            // Update dengan data dari tb_lokasi jika tersedia
            const finalData = await Promise.all(
                updatedData.map(async (item) => {
                    const lokasiInfo = findLokasiById(lokasiData, item.id_lokasi);

                    // Ambil Real Forcing dari Setara Urea Per Umur berdasarkan id_lokasi
                    const realForcingFromSetaraUrea = await fetchRealForcingFromSetaraUrea(item.id_lokasi);

                    // AMBIL TANGGAL CLOSING dari Monitor Hasil Panen berdasarkan id_lokasi
                    const tanggalClosingFromMonitor = await fetchTanggalClosingFromMonitorHasilPanen(item.id_lokasi);

                    // HITUNG UMUR - SAAT INI menggunakan fungsi dari calculationFormula.js
                    const umurHSFSaatIni = calculateUmurHSFSaatIni(
                        realForcingFromSetaraUrea,
                        tanggalClosingFromMonitor,
                        new Date() // tanggal saat ini
                    );

                    // HITUNG UMUR - SAAT MAPPING menggunakan fungsi dari calculationFormula.js
                    const umurHSFSaatMapping = calculateUmurHSFSaatMapping(
                        item.tanggal_selesai_identifikasi,
                        realForcingFromSetaraUrea
                    );

                    // AMBIL DATA MULAI_RAWAT dari tb_lokasi
                    const mulai_rawat = lokasiInfo ? lokasiInfo.mulai_rawat || null : null;

                    let baseData = {};

                    if (lokasiInfo) {
                        // Ambil data dari tb_lokasi
                        const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                        const displayLuas = lokasiInfo.netto || "-";
                        const displayTahun = lokasiInfo.tahun || "";
                        const displayMulaiRawat = lokasiInfo.mulai_rawat || "-";

                        // Default kepala kebun dan kepala wilayah dari tb_lokasi
                        const defaultKepalaKebun = lokasiInfo.kepala_kebun || "";
                        const defaultKepalaWilayah = lokasiInfo.kepala_wilayah || "";

                        // Coba ambil kepala kebun plant care dan kepala wilayah dari tb_pj_wilayah
                        const lokasiAdditionalData = displayLokasi
                            ? await fetchLokasiData(displayLokasi)
                            : {
                                  kepalaKebunPlantCare: null,
                                  kepalaWilayah: null,
                              };

                        // Gunakan kepala kebun plant care jika ada, jika tidak gunakan default dari tb_lokasi
                        const finalKepalaKebun = lokasiAdditionalData.kepalaKebunPlantCare || defaultKepalaKebun;

                        // Gunakan kepala wilayah dari tb_pj_wilayah jika ada, jika tidak gunakan default dari tb_lokasi
                        const finalKepalaWilayah = lokasiAdditionalData.kepalaWilayah || defaultKepalaWilayah;

                        // Hitung semua formula menggunakan calculationFormula dengan parameter mulai_rawat
                        const calculations = calculateAllFormulas(
                            {
                                ...item,
                                luas_netto: displayLuas,
                            },
                            mulai_rawat
                        );

                        // Hitung rencana saat panen
                        const rencanaSaatPanen = calculateRencanaSaatPanen(item.tanggal_terbang);

                        // Format Real Forcing untuk ditampilkan
                        const displayRealForcing = realForcingFromSetaraUrea
                            ? formatRealForcingForDisplay(realForcingFromSetaraUrea)
                            : "-";

                        baseData = {
                            ...item,
                            lokasi: displayLokasi,
                            luas_netto: displayLuas,
                            tahun: displayTahun,
                            mulai_rawat: displayMulaiRawat,
                            kepala_kebun: finalKepalaKebun,
                            kepala_wilayah: finalKepalaWilayah,
                            // Data Real Forcing dari Setara Urea Per Umur
                            real_forcing: displayRealForcing,
                            // Data calculated dari formula
                            week: calculations.week,
                            total_buah_alami: calculations.totalBuahAlami,
                            populasi_total: calculations.populasiTotal,
                            percentage_early_cones: calculations.percentageBuahAlami.earlyConesPercentage,
                            percentage_dry_petals: calculations.percentageBuahAlami.dryPetalsPercentage,
                            percentage_buah_alami: calculations.percentageBuahAlami.buahAlamiPercentage,
                            percentage_total_buah_alami: calculations.percentageBuahAlami.totalPercentage,
                            // Persentase berdasarkan populasi total
                            percentage_by_populasi_early_cones: calculations.percentagesByPopulasi.earlyConesPercentage,
                            percentage_by_populasi_dry_petals: calculations.percentagesByPopulasi.dryPetalsPercentage,
                            percentage_by_populasi_buah_alami: calculations.percentagesByPopulasi.buahAlamiPercentage,
                            percentage_by_populasi_total: calculations.percentagesByPopulasi.totalPercentage,
                            // Data rencana saat panen
                            rencana_early_cones: rencanaSaatPanen.earlyCones,
                            rencana_dry_petals: rencanaSaatPanen.dryPetals,
                            rencana_buah: rencanaSaatPanen.buah,
                            // UMUR - SAAT INI
                            umur_hsf_saat_ini: umurHSFSaatIni,
                            // UMUR - SAAT MAPPING
                            umur_hsf_saat_mapping: umurHSFSaatMapping,
                            // UMUR - TERBANG (ditambahkan) - sekarang dalam format object
                            umur_terbang_hari: calculations.umurTerbang.hari,
                            umur_terbang_bulan: calculations.umurTerbang.bulan,
                            umur_terbang_display: calculations.umurTerbang.display,
                        };
                    } else {
                        // Jika tidak ditemukan di tb_lokasi
                        const displayLokasi = item.id_lokasi || "";
                        const lokasiAdditionalData = displayLokasi
                            ? await fetchLokasiData(displayLokasi)
                            : {
                                  kepalaKebunPlantCare: null,
                                  kepalaWilayah: null,
                              };

                        // Hitung semua formula menggunakan calculationFormula (tanpa mulai_rawat)
                        const calculations = calculateAllFormulas(item);

                        // Hitung rencana saat panen
                        const rencanaSaatPanen = calculateRencanaSaatPanen(item.tanggal_terbang);

                        // Format Real Forcing untuk ditampilkan
                        const displayRealForcing = realForcingFromSetaraUrea
                            ? formatRealForcingForDisplay(realForcingFromSetaraUrea)
                            : "-";

                        baseData = {
                            ...item,
                            lokasi: displayLokasi,
                            luas_netto: "-",
                            tahun: "",
                            mulai_rawat: "-",
                            kepala_kebun: lokasiAdditionalData.kepalaKebunPlantCare || "",
                            kepala_wilayah: lokasiAdditionalData.kepalaWilayah || "",
                            // Data Real Forcing dari Setara Urea Per Umur
                            real_forcing: displayRealForcing,
                            // Data calculated dari formula
                            week: calculations.week,
                            total_buah_alami: calculations.totalBuahAlami,
                            populasi_total: calculations.populasiTotal,
                            percentage_early_cones: calculations.percentageBuahAlami.earlyConesPercentage,
                            percentage_dry_petals: calculations.percentageBuahAlami.dryPetalsPercentage,
                            percentage_buah_alami: calculations.percentageBuahAlami.buahAlamiPercentage,
                            percentage_total_buah_alami: calculations.percentageBuahAlami.totalPercentage,
                            // Persentase berdasarkan populasi total
                            percentage_by_populasi_early_cones: calculations.percentagesByPopulasi.earlyConesPercentage,
                            percentage_by_populasi_dry_petals: calculations.percentagesByPopulasi.dryPetalsPercentage,
                            percentage_by_populasi_buah_alami: calculations.percentagesByPopulasi.buahAlamiPercentage,
                            percentage_by_populasi_total: calculations.percentagesByPopulasi.totalPercentage,
                            // Data rencana saat panen
                            rencana_early_cones: rencanaSaatPanen.earlyCones,
                            rencana_dry_petals: rencanaSaatPanen.dryPetals,
                            rencana_buah: rencanaSaatPanen.buah,
                            // UMUR - SAAT INI
                            umur_hsf_saat_ini: umurHSFSaatIni,
                            // UMUR - SAAT MAPPING
                            umur_hsf_saat_mapping: umurHSFSaatMapping,
                            // UMUR - TERBANG (ditambahkan) - sekarang dalam format object
                            umur_terbang_hari: calculations.umurTerbang.hari,
                            umur_terbang_bulan: calculations.umurTerbang.bulan,
                            umur_terbang_display: calculations.umurTerbang.display,
                        };
                    }

                    return baseData;
                })
            );

            // PERUBAHAN: Urutkan data berdasarkan tanggal terbang terbaru setelah refresh
            const sortedData = sortDataByTanggalTerbang(finalData);

            return sortedData;
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Gunakan fungsi import dari file terpisah
            const result = await importExcelToFirebase(file, database, onValuePromise);

            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui.`,
                    severity: "success",
                });

                // Set last updated time
                setLastUpdated(new Date());

                // Refresh data setelah import berhasil
                const finalData = await refreshDataAfterImport();

                setDataList(finalData);
                setOriginalDataList(finalData);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            handleMenuClose();
            setImportLoading(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel((prev) => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterTahun) {
            filtered = filtered.filter((item) => item.tahun === filterTahun);
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterTahun("");
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses halaman ini</Alert>
            </Box>
        );
    }

    // Get unique years for filter dropdown
    const tahunList = [...new Set(originalDataList.map((item) => item.tahun))].filter(Boolean).sort();

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/*<ReportBuahAlami/>*/}

            {showFilterPanel && (
                <Paper sx={{ width: "100%", overflow: "hidden", p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tahun</InputLabel>
                            <Select value={filterTahun} label="Tahun" onChange={(e) => setFilterTahun(e.target.value)}>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {tahunList.map((tahun) => (
                                    <MenuItem key={tahun} value={tahun}>
                                        {tahun}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: "100%", overflow: "hidden", p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Data Drone Buah Alami
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari berdasarkan lokasi..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={handleImportButtonClick}
                            disabled={importLoading}
                        >
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{
                                maxHeight: 500,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="drone buah alami table">
                                <TableHead>
                                    {/* Headers */}
                                    <TableRow className="tr">
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Tanggal Terbang
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Lokasi
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Tanggal Selesai Identifikasi
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Week
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Luas Netto
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Kepala Wilayah
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Kepala Kebun
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Real Forcing
                                        </TableCell>
                                        <TableCell colSpan={3} className="ded-tch">
                                            Umur
                                        </TableCell>
                                        <TableCell colSpan={3} className="ded-tch">
                                            Buah Alami
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            Total Buah Alami
                                        </TableCell>
                                        <TableCell colSpan={3} className="ded-tch">
                                            % Buah Alami
                                        </TableCell>
                                        <TableCell rowSpan={2} className="ded-tch">
                                            % Total Buah Alami
                                        </TableCell>
                                        <TableCell colSpan={2} className="ded-tch">
                                            Populasi
                                        </TableCell>
                                        <TableCell colSpan={3} className="ded-tch">
                                            Rencana Saat Panen
                                        </TableCell>
                                    </TableRow>

                                    {/* Subheaders */}
                                    <TableRow className="tr">
                                        {/* Umur - 3 kolom */}
                                        <TableCell className="ded-tc">Terbang</TableCell>
                                        <TableCell className="ded-tc">Saat Ini</TableCell>
                                        <TableCell className="ded-tc">Saat Mapping</TableCell>

                                        {/* Buah Alami - 3 kolom */}
                                        <TableCell className="ded-tc">Early Cones</TableCell>
                                        <TableCell className="ded-tc">Dry Petals</TableCell>
                                        <TableCell className="ded-tc">Buah</TableCell>

                                        {/* % Buah Alami - 3 kolom */}
                                        <TableCell className="ded-tc">Early Cones</TableCell>
                                        <TableCell className="ded-tc">Dry Petals</TableCell>
                                        <TableCell className="ded-tc">Buah</TableCell>

                                        {/* Populasi - 2 kolom */}
                                        <TableCell className="ded-tc">Per Ha</TableCell>
                                        <TableCell className="ded-tc">Total</TableCell>

                                        {/* Rencana Saat Panen - 3 kolom */}
                                        <TableCell className="ded-tc">Early Cones</TableCell>
                                        <TableCell className="ded-tc">Dry Petals</TableCell>
                                        <TableCell className="ded-tc">Buah</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.firebaseKey}>
                                                {/* Tanggal Terbang */}
                                                <TableCell>{formatDateDisplay(row.tanggal_terbang)}</TableCell>

                                                {/* Lokasi dan Id Lokasi */}
                                                <TableCell sx={{ cursor: "pointer", position: "relative" }}>
                                                    <MapActionTooltip
                                                        database={database}
                                                        id_lokasi={row.id_lokasi}
                                                        lokasiNama={row.lokasi}
                                                        firebaseKey={row.firebaseKey}
                                                        onDataUpdated={handleDataUpdated}
                                                    >
                                                        <>
                                                            <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                                                                {formatText(row.lokasi)}
                                                            </div>
                                                            <div style={{ fontSize: "0.8em", color: "#666" }}>
                                                                ID: {row.id_lokasi || "-"}
                                                            </div>
                                                            <div style={{ fontSize: "0.8em", color: "#888" }}>
                                                                Mulai Rawat: {formatDateDisplay(row.mulai_rawat)}
                                                            </div>
                                                        </>
                                                    </MapActionTooltip>
                                                </TableCell>

                                                {/* Tanggal Selesai Identifikasi */}
                                                <TableCell>
                                                    {formatDateDisplay(row.tanggal_selesai_identifikasi)}
                                                </TableCell>

                                                {/* Week */}
                                                <TableCell>{formatText(row.week)}</TableCell>

                                                {/* Luas Netto */}
                                                <TableCell>{formatText(row.luas_netto)}</TableCell>

                                                {/* Kepala Wilayah */}
                                                <TableCell>{formatText(row.kepala_wilayah)}</TableCell>

                                                {/* Kepala Kebun */}
                                                <TableCell>{formatText(row.kepala_kebun)}</TableCell>

                                                {/* Real Forcing - dari Setara Urea Per Umur */}
                                                <TableCell>{formatText(row.real_forcing)}</TableCell>

                                                {/* Umur - Terbang (PERUBAHAN: menampilkan dalam format "108 (3,6)") */}
                                                <TableCell>
                                                    {row.umur_terbang_bulan && row.umur_terbang_bulan !== '-'
                                                        ? row.umur_terbang_bulan
                                                        : '-'}
                                                </TableCell>

                                                {/* Umur - Saat Ini */}
                                                <TableCell>
                                                    {row.umur_hsf_saat_ini && row.umur_hsf_saat_ini !== "-"
                                                        ? formatDecimal(parseFloat(row.umur_hsf_saat_ini))
                                                        : row.umur_hsf_saat_ini || "-"}
                                                </TableCell>

                                                {/* Umur - Saat Mapping */}
                                                <TableCell>
                                                    {row.umur_hsf_saat_mapping && row.umur_hsf_saat_mapping !== "-"
                                                        ? formatDecimal(parseFloat(row.umur_hsf_saat_mapping))
                                                        : row.umur_hsf_saat_mapping || "-"}
                                                </TableCell>

                                                {/* Buah Alami - Early Cones */}
                                                <TableCell>{formatNumber(row.early_cones)}</TableCell>

                                                {/* Buah Alami - Dry Petals */}
                                                <TableCell>{formatNumber(row.dry_petals)}</TableCell>

                                                {/* Buah Alami - Buah */}
                                                <TableCell>{formatNumber(row.buah_alami)}</TableCell>

                                                {/* Total Buah Alami */}
                                                <TableCell>{formatText(row.total_buah_alami)}</TableCell>

                                                {/* % Buah Alami - Early Cones */}
                                                <TableCell>
                                                    {formatText(row.percentage_by_populasi_early_cones)}
                                                </TableCell>

                                                {/* % Buah Alami - Dry Petals */}
                                                <TableCell>
                                                    {formatText(row.percentage_by_populasi_dry_petals)}
                                                </TableCell>

                                                {/* % Buah Alami - Buah */}
                                                <TableCell>
                                                    {formatText(row.percentage_by_populasi_buah_alami)}
                                                </TableCell>

                                                {/* % Total Buah Alami */}
                                                <TableCell>{formatText(row.percentage_by_populasi_total)}</TableCell>

                                                {/* Populasi - Per Ha */}
                                                <TableCell>{formatNumber(row.populasi_per_ha)}</TableCell>

                                                {/* Populasi - Total */}
                                                <TableCell>{formatNumber(row.populasi_total)}</TableCell>

                                                {/* Rencana Saat Panen - Early Cones */}
                                                {/* Tanggal Terbang + 85 hari */}
                                                <TableCell>{formatText(row.rencana_early_cones)}</TableCell>

                                                {/* Rencana Saat Panen - Dry Petals */}
                                                {/* Tanggal Terbang + 55 hari */}
                                                <TableCell>{formatText(row.rencana_dry_petals)}</TableCell>

                                                {/* Rencana Saat Panen - Buah */}
                                                {/* Tanggal Terbang + 30 hari */}
                                                <TableCell>{formatText(row.rencana_buah)}</TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>

                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>

                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{
                                    flexShrink: 0,
                                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                        margin: 0,
                                    },
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 200,
                            border: "1px dashed #e0e0e0",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery
                                ? "Tidak ada data yang sesuai dengan pencarian"
                                : "Tidak ada data drone buah alami."}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default DroneBuahAlami;